import { Alert, Spinner } from '@recipes/ui';
import { Component, Suspense, lazy, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import type { TastingMapProps } from './tasting-map';

const TastingMap = lazy(() => import('./tasting-map'));

class MapErrorBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

/**
 * Code-split map. Offline (or when the map cannot start, e.g. no WebGL) a notice is shown
 * instead – every screen that embeds the map offers the same content as a list.
 */
export function LazyTastingMap(props: TastingMapProps) {
  const { t } = useTranslation();
  const online = useOnlineStatus();
  if (!online)
    return (
      <Alert variant="info" title={t('tasting.map.offlineTitle')} data-testid="map-offline">
        {t('tasting.map.offlineBody')}
      </Alert>
    );
  const unavailable = (
    <Alert variant="warning" title={t('tasting.map.unavailableTitle')}>
      {t('tasting.map.unavailableBody')}
    </Alert>
  );
  return (
    <MapErrorBoundary fallback={unavailable}>
      <Suspense
        fallback={
          <div className="flex h-80 items-center justify-center rounded-lg border">
            <Spinner label={t('app.loading')} />
          </div>
        }
      >
        <TastingMap {...props} />
      </Suspense>
      <p className="mt-1 text-xs text-muted-foreground">{t('tasting.map.privacyHint')}</p>
    </MapErrorBoundary>
  );
}
